import { useEffect, useRef, useState } from 'react';
import type { StepProps } from '../../OBWWizard';

// Phase 5c.5 (ADR-030 D3) — partner T&Cs display + scroll-to-bottom-to-enable
// + "Accept Terms" CTA. T&Cs inlined here; future versioning + accept-record
// is a Phase 6 follow-up.

const TERMS_VERSION = '2026-05-24';

export function PartnerTermsStep({ data, onNext }: StepProps) {
  const alreadyAccepted = !!data?.termsAccepted;
  const [scrolledToEnd, setScrolledToEnd] = useState(alreadyAccepted);
  const [accepted, setAccepted] = useState(alreadyAccepted);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || scrolledToEnd) return;
    function onScroll() {
      if (!el) return;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
      if (atBottom) setScrolledToEnd(true);
    }
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [scrolledToEnd]);

  function commit() {
    setAccepted(true);
    onNext({ termsAccepted: true, termsVersion: TERMS_VERSION });
  }

  return (
    <div className="obw-step-content">
      <h2 className="obw-title">Partner programme terms</h2>
      <p className="obw-subtitle">
        Read through the partner terms below. Scroll to the bottom to enable the accept button.
      </p>

      <div
        ref={scrollRef}
        className="obw-auth-box"
        style={{ maxHeight: '420px', overflowY: 'auto', padding: '20px 24px', fontSize: '14px', lineHeight: 1.7, color: 'var(--text)' }}
        tabIndex={0}
        aria-label="Partner programme terms and conditions"
      >
        <p style={{ marginTop: 0 }}><strong>Effective: 24 May 2026 · Version {TERMS_VERSION}</strong></p>
        <p>
          These Nexanomics Partner Programme Terms govern your participation as a referral and
          reseller partner. By accepting these terms you agree to be bound by them in addition to
          the Nexanomics Subscriber Terms of Service already accepted at subscriber sign-up.
        </p>

        <p><strong>1. Programme scope.</strong> The partner programme allows approved subscribers to
        refer new customers to Nexanomics in exchange for an ongoing referral commission. Partners
        receive a co-branded reseller portal for managing their customer accounts.</p>

        <p><strong>2. Commission schedule.</strong> Approved partners earn 15% of monthly recurring
        revenue from each successfully referred customer for the lifetime of the customer's
        Nexanomics subscription. Commissions are paid monthly in arrears via Stripe Connect.
        Detailed commission terms are published in the partner dashboard once approved.</p>

        <p><strong>3. Brand usage.</strong> Partners may use the "Nexanomics Partner" badge on
        their own website and marketing materials. Usage must follow the Nexanomics Brand
        Guidelines, available in the partner dashboard. No co-branding outside these guidelines is
        permitted without written approval.</p>

        <p><strong>4. Backlink requirement.</strong> Approved partners agree to add a backlink to
        nexanomics.com from their primary business website within 30 days of approval. The
        backlink is the attribution mechanism for the commission system; partners with no
        backlink at 60 days post-approval may be moved to inactive status pending review.</p>

        <p><strong>5. Support obligations.</strong> Partners are first-line support for their
        referred customers. Nexanomics provides partner-level escalation channels, documentation,
        and training resources. Response-time SLAs are 1 business day for partner-tier accounts.</p>

        <p><strong>6. Suitability check.</strong> All applications go through a Nexanomics
        suitability assessment evaluating internal NEXA usage, business identity, web presence,
        and portfolio. The assessment may auto-approve, escalate to staff review, or auto-reject.
        Applicants in the staff-review band receive a decision within 1 business day.</p>

        <p><strong>7. Termination.</strong> Either party may terminate the partner relationship
        with 30 days' written notice. Outstanding commissions accrued before termination remain
        payable per the standard schedule.</p>

        <p><strong>8. Data &amp; privacy.</strong> Partner application data (business name,
        website, registration number, description of practice) is processed under the Nexanomics
        Privacy Policy. Suitability check inputs are retained for audit and may be re-evaluated
        if you re-apply.</p>

        <p><strong>9. Governing law.</strong> These terms are governed by the laws of South
        Australia, Australia. Disputes are subject to the exclusive jurisdiction of the courts of
        South Australia.</p>

        <p><strong>10. Changes.</strong> Nexanomics may amend these terms with 30 days' notice
        via the partner dashboard. Continued participation after the notice period constitutes
        acceptance.</p>

        <p style={{ marginBottom: 0, fontStyle: 'italic', color: 'var(--text-dim)' }}>
          End of terms · v{TERMS_VERSION}
        </p>
      </div>

      {!scrolledToEnd && (
        <p className="obw-form-hint" style={{ marginTop: '12px' }}>
          Scroll to the bottom of the terms to continue.
        </p>
      )}

      <div className="portal-wizard-nav">
        <button
          type="button"
          className="obw-btn-primary"
          onClick={commit}
          disabled={!scrolledToEnd}
          aria-disabled={!scrolledToEnd}
        >
          {accepted ? '✓ Terms accepted' : 'Accept terms →'}
        </button>
      </div>
    </div>
  );
}
